import React from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { SimpleListActionsButton } from 'components/lib/SimpleListModalComponents';

import { MAILBOX_CONNECTION_STATUS, MAILBOX_CONNECTION_STATUS_LABELS } from '../../constants';

export const compactContactTableStyles = css`
  .ant-table-tbody > tr > td {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .ant-table-body {
    scrollbar-width: thin;
    scrollbar-color: var(--gray-dark) transparent;
  }

  .ant-table-body::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .ant-table-body::-webkit-scrollbar-track {
    border-radius: 100px;
    background-color: transparent;
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: var(--gray-dark);
  }

  .ant-table-body::-webkit-scrollbar-button {
    display: none;
  }
`;

export const fillContactTableStyles = css`
  display: flex;
  flex: 1;
  min-height: 0;
  margin-right: 8px;

  &&&&&& {
    .ant-spin-nested-loading {
      width: 100%;
      height: 100%;
    }

    .ant-spin-container,
    .ant-table,
    .ant-table-container {
      height: 100%;
    }

    .ant-table {
      display: flex;
      flex-direction: column;
      width: 100%;
      box-sizing: border-box;
    }

    .ant-table-container {
      display: flex;
      min-height: 0;
      flex-direction: column;
    }

    .ant-table-header {
      flex-shrink: 0;
    }

    .ant-table-body {
      flex: 1;
      min-height: 0;
      max-height: none !important;
    }
  }
`;

export const ValueText = styled.span`
  display: inline-block;
  color: var(--text-primary);
`;

export const OwnerText = styled(ValueText)`
  white-space: nowrap;
`;

const IdentityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const IdentityPrimary = styled.span`
  color: var(--text-primary);
  font-family: var(--font-bold);
`;

const IdentitySecondary = styled.span`
  color: var(--text-secondary);
  font-size: 10px;
`;

export const MutedText = styled.span`
  display: inline-block;
  color: var(--text-secondary);
`;

const StatusBadge = styled.span<{ status?: string | null }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 1px 8px;
  white-space: nowrap;
  border: 1px solid
    ${({ status }) => {
      if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
        return 'var(--positive-green)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
        return 'var(--negative-orange)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
        return 'var(--error-red)';
      }

      return 'var(--line-gray)';
    }};
  border-radius: 999px;
  font-size: 11px;
  color: ${({ status }) => {
    if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
      return 'var(--positive-green)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
      return 'var(--negative-orange)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
      return 'var(--error-red)';
    }

    return 'var(--text-secondary)';
  }};
`;

const StatusBadgeContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const CompactActionButton = styled(SimpleListActionsButton)`
  && {
    width: 88px;
    height: 28px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

export const renderContactIdentity = (record: { name?: string; email?: string | null; phone?: string | null }) => {
  const name = record.name?.trim();
  const phone = record.phone?.trim();

  return (
    <IdentityCell>
      {name ? (
        <>
          <IdentityPrimary>{name}</IdentityPrimary>
          {record.email ? <IdentitySecondary>{record.email}</IdentitySecondary> : null}
        </>
      ) : (
        <IdentityPrimary>{record.email || phone || 'Shopper contact'}</IdentityPrimary>
      )}
      {phone && (name || record.email) ? <IdentitySecondary>{phone}</IdentitySecondary> : null}
    </IdentityCell>
  );
};

export const renderMailboxStatus = (status: string | null | undefined, email?: string | null, isSyncing = false) => (
  <StatusBadge status={status}>
    <StatusBadgeContent>
      {isSyncing ? <LoadingOutlined /> : null}
      <span>
        {isSyncing
          ? 'Syncing'
          : email
            ? (MAILBOX_CONNECTION_STATUS_LABELS[status || 'default'] ?? MAILBOX_CONNECTION_STATUS_LABELS.default)
            : 'No email'}
      </span>
    </StatusBadgeContent>
  </StatusBadge>
);
